import React, { Fragment, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Button, Row, Col, Form } from 'react-bootstrap';
import classNames from 'classnames';
import moment from 'moment';

import {
  requestList as request,
  requestDelete,
  requestDeletes,
  requestInform
} from 'api/sfms/product/output';
import ActionButton from 'components/common/ActionButton';
import CheckPlant from 'components/common-sfims/CheckPlant';
import MonthPicker from 'components/common-sfims/form/MonthPicker';
import Select from 'components/common-sfims/form/Select';
import ListInContent from 'components/common-sfims/ListInContent';
import columns from 'components/pages/sfms/product/output/columns';
import Import from 'components/pages/sfms/product/output/Import';
import Init from 'components/pages/sfms/product/output/Init';
import Inform from 'components/pages/sfms/product/output/Inform';
import Upload from 'components/pages/sfms/product/output/Upload';
import { datas2ExcelJSON, utc2tz } from 'function/convert';
import { onListClick } from 'function/event';
import { downloadExcelByJSON } from 'function/file';
import { getText } from 'function/language';
import { getLoginInform } from 'function/login';
import { getErrorMessage } from 'function/message';
import useCheckPlantState from 'global/checkPlantState';
import { useConfirmModalState } from 'global/confirmModalState';
import usePlantState from 'global/plantState';
import useListScroll from 'hooks/useListScroll';
import useObjReducer from 'hooks/useObjReducer';
import { RESPONSE_CODE, KOREA_BU } from 'types/const';
import { CODE_SEARCH_TYPE, RETURN_YN } from 'types/type';
import pluck from 'utils/array/pluck';
import { comma, number as formatNumber } from 'utils/format';
import format from 'utils/string/format';

/**
 * 배합비 작성 정보 관리 > 제품 생산량 - 목록
 */
const List = () => {
  /* variable */
  const { adminYn, fatoryCodeSeq } = getLoginInform();

  /* hook */
  const { end, setEnd, total, setTotal } = useListScroll();

  /* router */
  const { state } = useLocation();

  /* recoil */
  const { checkPlant } = useCheckPlantState();
  const { setConfirmModal } = useConfirmModalState();
  const {
    plant: { hasReads: hasReadPlants }
  } = usePlantState();

  /* state */
  // 코드 검색어
  const [cd, setCd] = useState(state?.prdtCd ?? state?.mixCd ?? '');
  // 목록
  const [datas, setDatas] = useState([]);
  // 다중 삭제 비활성화 여부
  const [isDisableDeletes, setIsDisableDeletes] = useState(false);
  // 해외 공장 여부
  const [isForeignPlant, setIsForeignPlant] = useState(false);
  // 로딩 여부
  const [isLoading, setIsLoading] = useState(true);
  // 설정 모달 여부
  const [isSetting, setIsSetting] = useState(false);
  // SAP에서 가져오기 모달 표시 여부
  const [isShowImport, setIsShowImport] = useState(false);
  // 정보 모달 표시 여부
  const [isShowInform, setIsShowInform] = useState(false);
  // 초기화 모달 표시 여부
  const [isShowInit, setIsShowInit] = useState(false);
  // 업로드 모달 표시 여부
  const [isShowUpload, setIsShowUpload] = useState(false);
  // 메시지
  const [message, setMessage] = useState({});
  // 제품 생산량 목록 조회 요청 데이터
  const [params, setParams] = useObjReducer({
    factoryCodeSeqList: checkPlant,
    prodYm: moment()
      .add(moment().date() >= 22 ? 1 : 0, 'months')
      .format('YYYYMM'),
    searchType: CODE_SEARCH_TYPE.PRDT,
    ...state
  });
  // 선택한 월
  const [prodYm, setProdYm] = useState(params.prodYm);
  // 선택한 코드 구분
  const [searchType, setSearchType] = useState(params.searchType);
  // 선택한 번호
  const [selectSeq, setSelectSeq] = useState(0);
  // 선택한 번호 목록
  const [seqList, setSeqList] = useState([]);

  /* watch */
  useEffect(() => {
    setIsForeignPlant(
      hasReadPlants.find(({ seq } = {}) => seq === fatoryCodeSeq)?.buCode !==
        KOREA_BU
    );
  }, [hasReadPlants]);
  useEffect(() => {
    requestList();
  }, [params]);

  /* method */
  // 제품 생산량 목록 조회 요청
  const requestList = async () => {
    setEnd(20);
    setTotal(0);
    setDatas([]);
    setIsLoading(true);
    setMessage({});
    setSeqList([]);

    try {
      const {
        list = [],
        totalCount = 0,
        code,
        message
      } = await request({
        ...params,
        [searchType === CODE_SEARCH_TYPE.MIX ? 'mixCd' : 'prdtCd']: cd
      });

      if (code !== RESPONSE_CODE.Success) {
        throw {
          code,
          error: message
        };
      }

      setTotal(totalCount);
      setDatas(
        list.map((value = {}, index) => ({
          ...value,
          rowNum: totalCount - index,
          regDt: utc2tz(value.regDt)
        }))
      );
    } catch (error) {
      setMessage(error);
    } finally {
      setIsLoading(false);
    }
  };
  // 정보 모달 표시
  const showInform = (seq, isSetting = false) => {
    setIsSetting(isSetting);
    setIsShowInform(true);
    setSelectSeq(seq);
  };

  /* event */
  // 전체 체크 변경 시
  const onAllCheckChange = ({ target: { checked } }) => {
    let checks = [];

    if (checked) {
      checks = pluck(datas.slice(0, end), 'seq');
    }

    setSeqList(checks);
  };
  // 코드 구분 변경 시
  const onChangeSearchType = ({ target: { value } }) => {
    const searchType = Math.floor(value);

    setParams({
      factoryCodeSeqList: checkPlant,
      prodYm,
      searchType
    });
    setSearchType(searchType);
  };
  // 체크 변경 시
  const onCheckChange = ({ target: { checked, value } }) => {
    value = Math.floor(value);

    if (checked) {
      setSeqList([...seqList, value]);

      return;
    }

    setSeqList(seqList.filter(check => check !== value));
  };
  // 삭제 클릭 시
  const onDeleteClick = seq => {
    setConfirmModal({
      isOpen: true,
      title: 'delete',
      message: 'deleteMessage',
      isCancelBtn: true,
      btnNm: 'delete',
      async onConfirm() {
        try {
          // 제품 생산량 삭제 요청 데이터로 제품 목록이 필요하기 때문에 제품 생산량 정보 조회 요청
          const {
            data: { productOutturnList = [] } = {},
            code: informCode,
            message: informMessage
          } = await requestInform(seq, { prodYm: params.prodYm });

          if (informCode !== RESPONSE_CODE.Success) {
            throw {
              code: informCode,
              error: informMessage
            };
          }

          // 제품 생산량 삭제 요청
          const { code, message } = await requestDelete(seq, {
            productOutturnList: productOutturnList.reduce(
              (result, { seq } = {}) => ({
                ...result,
                seq
              }),
              {}
            )
          });

          if (code !== RESPONSE_CODE.Success) {
            throw {
              code,
              error: message
            };
          }

          // 제품 생산량 목록 조회 요청
          await requestList();
        } catch (error) {
          setConfirmModal({
            isOpen: true,
            message: getErrorMessage(error?.code, error?.error)
          });
        }
      }
    });
  };
  // 다중 삭제 클릭 시
  const onDeletesClick = () => {
    setConfirmModal({
      isOpen: true,
      title: 'deletes',
      message: 'deleteMessage',
      isCancelBtn: true,
      btnNm: 'delete',
      async onConfirm() {
        setIsDisableDeletes(true);

        try {
          // 제품 생산량 다중 삭제 요청
          const { code, message } = await requestDeletes({ seqList });

          if (code !== RESPONSE_CODE.Success) {
            throw {
              code,
              error: message
            };
          }

          // 제품 생산량 목록 조회 요청
          await requestList();
        } catch (error) {
          setConfirmModal({
            isOpen: true,
            message: getErrorMessage(error?.code, error?.error)
          });
        } finally {
          setIsDisableDeletes(false);
        }
      }
    });
  };
  // 엑셀 다운로드 클릭 시
  const onDownloadExcelClick = () => {
    downloadExcelByJSON(
      datas2ExcelJSON(
        datas,
        Object.fromEntries(
          Object.entries(columns).filter(([key]) => key !== 'updateOrDelete')
        )
      ),
      [getText('productOutput'), moment(params.prodYm).format('YYMM')]
        .join('_')
        .concat('.xlsx')
    );
  };
  // 검색 클릭 시
  const onSearchClick = event => {
    event.preventDefault();

    setParams({
      factoryCodeSeqList: checkPlant,
      prodYm,
      searchType
    });
  };

  /* view */
  return (
    <>
      <ListInContent
        columns={[
          <Form.Check
            key="allCheck"
            checked={
              seqList.length > 0 &&
              seqList.length === datas.slice(0, end).length
            }
            onChange={onAllCheckChange}
          />,
          ...Object.keys(columns).map(key =>
            key === 'amount' ? `${getText(key)} (kg)` : key
          )
        ]}
        message={message}
        showLoading={isLoading}
      >
        <Fragment key="headerButton">
          {(adminYn === RETURN_YN.Y || !isForeignPlant) && (
            <Button
              size="sm"
              className="ms-2"
              onClick={() => setIsShowImport(true)}
            >
              {getText('importSAP')}
            </Button>
          )}
          {(adminYn === RETURN_YN.Y || isForeignPlant) && (
            <Button
              size="sm"
              className="ms-2"
              onClick={() => setIsShowUpload(true)}
            >
              {getText('uploadExcel')}
            </Button>
          )}
          <Button
            size="sm"
            className="ms-2"
            onClick={() => showInform(0, true)}
          >
            + {getText('insert')}
          </Button>
        </Fragment>
        <Fragment key="search">
          <CheckPlant value={params.factoryCodeSeqList} />
          <Form onSubmit={onSearchClick}>
            <Row className="ms-0 me-0 mt-3">
              <Col sm={1} className="ps-1 pe-1">
                <MonthPicker value={prodYm} onUpdate={setProdYm} />
              </Col>
              <Col sm={2} className="ps-1 pe-1">
                <Select
                  datas={[
                    {
                      text: 'prdtCd',
                      value: CODE_SEARCH_TYPE.PRDT
                    },
                    {
                      text: 'mixCd',
                      value: CODE_SEARCH_TYPE.MIX
                    }
                  ]}
                  value={searchType}
                  className="w-100"
                  required
                  onChange={onChangeSearchType}
                />
              </Col>
              <Col sm={1} className="ps-1 pe-1">
                <Form.Control
                  size="sm"
                  type="text"
                  value={cd}
                  autoFocus
                  placeholder={getText('code')}
                  onChange={({ target: { value } }) => setCd(value)}
                />
              </Col>
              <Col
                sm="auto"
                className="ps-1 pe-1 d-flex flex-row align-items-center"
              >
                <Button disabled={isLoading} size="sm" type="submit">
                  {getText('search')}
                </Button>
                <p className="mb-0 ms-3 fs--1 fw-bold lh-1">
                  {format(getText('total'), { total: comma(total) })}
                </p>
                {datas.length > 0 && (
                  <Button
                    size="sm"
                    variant="secondary"
                    className="ms-2"
                    onClick={onDownloadExcelClick}
                  >
                    {getText('downloadExcel')}
                  </Button>
                )}
                <Button
                  disabled={seqList.length === 0 || isDisableDeletes}
                  size="sm"
                  variant="danger"
                  className="ms-2"
                  onClick={onDeletesClick}
                >
                  {getText('deletes')}
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  className="ms-2"
                  onClick={() => setIsShowInit(true)}
                >
                  {getText('init')}
                </Button>
              </Col>
            </Row>
          </Form>
        </Fragment>
        <Fragment key="datas">
          {datas.slice(0, end).map((value = {}) => (
            <tr
              key={`datas${value.seq}`}
              className={classNames({
                'bg-200':
                  value.mixUseYn === RETURN_YN.N || value.useYn === RETURN_YN.N
              })}
              onClick={event => {
                onListClick(event, () => showInform(value.mixSeq));
              }}
            >
              <td>
                <Form.Check
                  checked={seqList.includes(value.seq)}
                  value={value.seq}
                  onChange={onCheckChange}
                />
              </td>
              {Object.entries(columns).map(([key, { type, format } = {}]) => (
                <td
                  key={`datas${value.seq}.${key}`}
                  className={classNames({
                    'cursor-pointer': key !== 'updateOrDelete',
                    'text-end': type === 'number'
                  })}
                >
                  {key === 'updateOrDelete' ? (
                    <>
                      <ActionButton
                        title={getText('update')}
                        icon="edit"
                        variant="action"
                        className="p-0 lh-1"
                        onClick={() => showInform(value.mixSeq, true)}
                      />
                      <ActionButton
                        title={getText('delete')}
                        icon="trash-alt"
                        variant="action"
                        className="p-0 lh-1 ms-2"
                        onClick={() => onDeleteClick(value.mixSeq)}
                      />
                    </>
                  ) : type === 'number' ? (
                    formatNumber(value[key], format)
                  ) : (
                    value[key]
                  )}
                </td>
              ))}
            </tr>
          ))}
        </Fragment>
      </ListInContent>
      <Import
        show={isShowImport}
        onHide={() => setIsShowImport(false)}
        onImport={requestList}
      />
      <Upload
        show={isShowUpload}
        onHide={() => setIsShowUpload(false)}
        onUpload={requestList}
      />
      <Init
        show={isShowInit}
        onHide={() => setIsShowInit(false)}
        onInit={requestList}
      />
      <Inform
        show={isShowInform}
        seq={selectSeq}
        setting={isSetting}
        setSetting={setIsSetting}
        prodYm={params.prodYm}
        onHide={() => setIsShowInform(false)}
        onSetting={requestList}
      />
    </>
  );
};

export default List;
